import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import isTouchDevice from 'is-touch-device';
import ChevronUpIcon from '@moda/icons/chevron-up-12';
import ChevronDownIcon from '@moda/icons/chevron-down-12';
import ShippingAltIcon from '@moda/icons/shipping-alt-20';
import { useParams } from 'react-router-dom';
import { Link } from '../../Link';
import { TrunkshowCountdownTimer } from '../../TrunkshowCountdownTimer';
import { PdpInfoContainerFragment_trunkshows_edges_node as PdpTrunkshow } from '../../../generated/types/PdpInfoContainerFragment';
import { hrefFor } from '../../../routers';

import './PdpTrunkshowInfo.scss';

interface Props {
  trunkshow: PdpTrunkshow;
  shippingDateFormatted?: string;
}

export const PdpTrunkshowInfo: React.FC<Props> = ({ trunkshow, shippingDateFormatted }) => {
  const { vertical = 'women' } = useParams<{ vertical?: undefined }>();
  const [isOpen, setIsOpen] = useState(false);
  const toggleInfo = useCallback((): void => setIsOpen(!isOpen), [isOpen]);

  return (
    <div className="PdpTrunkshowInfo" data-testid="infoModuleSection">
      <div className="PdpTrunkshowInfo__title">
        This item is part of the{' '}
        <Link
          to={hrefFor.TrunkshowPage({
            vertical,
            trunkshowId: trunkshow.id,
            trunkshowSlug: trunkshow.slug as string
          })}
          className="PdpTrunkshowInfo__title-link"
        >
          {trunkshow.name} {trunkshow.seasonName}
        </Link>{' '}
        Trunkshow
      </div>

      <div className="PdpTrunkshowInfo__trunkshow-info">
        <div className="PdpTrunkshowInfo__shipping">
          <ShippingAltIcon className="PdpTrunkshowInfo__shipping_icon" />
          Ships by {shippingDateFormatted}
        </div>

        {trunkshow?.activeEndI && (
          <TrunkshowCountdownTimer
            className="PdpTrunkshowInfo__timer"
            endingText="Ends in "
            countdownTime={trunkshow?.activeEndI}
            countdownEndMessage="This Trunkshow Has Expired"
            hasZeroes={false}
            hasIcon
          />
        )}
      </div>

      <div
        className={classNames('PdpTrunkshowInfo__card-content', {
          'PdpTrunkshowInfo__card-content--hovered': isOpen
        })}
        onMouseEnter={() => toggleInfo()}
        onMouseLeave={() => toggleInfo()}
        onClick={() => isTouchDevice() && toggleInfo()}
        onKeyPress={() => isTouchDevice() && toggleInfo()}
        role="button"
        tabIndex={0}
      >
        <div className="PdpTrunkshowInfo__card-title">
          What is Trunkshow?
          <ChevronUpIcon className="PdpTrunkshowInfo__icon-up" />
          <ChevronDownIcon className="PdpTrunkshowInfo__icon-down" />
        </div>

        <div className="PdpTrunkshowInfo__description">
          Trunkshow is a limited-time opportunity to pre-order next season&apos;s style, ahead of
          anyone else.
        </div>

        <div className="PdpTrunkshowInfo__description">
          Your order supports the designers and their collections - including pieces that might
          never make it to retail.
        </div>
      </div>
    </div>
  );
};
